'use client'

import { useEffect, useMemo, useState } from 'react'
import { fallbackVariant, findVariant, hasOptions, isVariantInStock, optionVariants, type OptionType, type OptionValues } from '../variantOptions'

export function useVariantSelection(product: any) {
  const [selectedVariantId, setSelectedVariantIdState] = useState<string | null>(null)
  // Set only when the shopper picks an option value (color / size / custom);
  // until then the selection mirrors the selected variant. It may name a
  // combination that has no variant — the picker shows it crossed out.
  const [pickedOptions, setPickedOptions] = useState<OptionValues | null>(null)

  const selectedVariant = useMemo(() => {
    if (!product) return null
    return selectedVariantId
      ? product?.variants?.find((v: any) => v.id === selectedVariantId) || product?.variant
      : product?.variant
  }, [product, selectedVariantId])

  const isVariantAvailable = (variant: any) => isVariantInStock(product, variant)

  const availableVariants = useMemo(() => {
    return product?.variants?.filter((v: any) => isVariantAvailable(v)) || []
  }, [product])

  useEffect(() => {
    if (!product?.variants?.length || selectedVariantId) return
    // Option products never start on a variant the picker can't reach (no
    // optionValues, e.g. added through the standalone variant API). Legacy
    // products keep the full list: `reachable` is empty for them.
    const reachable = hasOptions(product) ? optionVariants(product) : []
    const pool = reachable.length > 0 ? reachable : product.variants
    const poolAvailable = reachable.length > 0 ? reachable.filter((v: any) => isVariantAvailable(v)) : availableVariants
    const defaultVariant = product.variant || product.variants.find((v: any) => v.isDefault)
    const defaultReachable = reachable.length === 0 || reachable.some((v: any) => v.id === defaultVariant?.id)
    if (defaultVariant && defaultReachable && isVariantAvailable(defaultVariant)) {
      setSelectedVariantIdState(defaultVariant.id)
    } else if (poolAvailable.length > 0) {
      setSelectedVariantIdState(poolAvailable[0].id)
    } else {
      setSelectedVariantIdState(pool[0].id)
    }
  }, [product?.variants, selectedVariantId])

  const selectedOptions: OptionValues = pickedOptions ?? selectedVariant?.optionValues ?? {}
  const isSelectionPurchasable = !hasOptions(product) || !!findVariant(product, selectedOptions)

  // Flat pickers choose a variant directly, which also drops picked options.
  const setSelectedVariantId = (id: string | null) => {
    setPickedOptions(null)
    setSelectedVariantIdState(id)
  }

  const selectOption = (type: OptionType, value: string) => {
    const next: OptionValues = { ...selectedOptions, [type]: value }
    setPickedOptions(next)
    const variant = findVariant(product, next) ?? fallbackVariant(product, next, type)
    if (variant) setSelectedVariantIdState(variant.id)
  }

  return {
    selectedVariantId,
    setSelectedVariantId,
    selectedVariant,
    isVariantAvailable,
    availableVariants,
    selectedOptions,
    selectOption,
    isSelectionPurchasable,
  }
}
